import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { debounceTime, takeUntil } from 'rxjs';
import { AbstractComponent } from '../../../commons/abstract.component';
import { Channel } from '../../../models/channel.model';
import { Department } from '../../../models/department.model';
import { AttendanceDistributionEnum } from '../../../models/enum/attendance-distribution.type';
import { SettingsAttendance } from '../../../models/settings-attendance.model';
import { SettingsModule } from '../../../models/settings-module.model';
import { DepartmentService } from '../../../services/department.service';
import { FlowService } from '../../../services/flow.service';
import { SettingsAttendanceService } from '../../../services/settings-attendance.service';
import { SettingsModuleService } from '../../../services/settings-module.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { IrrelevanceReopeningModalComponent } from './irrelevance-reopening-modal/irrelevance-reopening-modal.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'settings-attendance-component',
    // templateUrl: './settings-attendance.component.html',
    templateUrl: './settings-attendance.v2.component.html',
    styleUrls: ['settings-attendance.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SettingsAttendanceComponent extends AbstractComponent implements OnInit, OnChanges {
  filterControlDepartmentToTransfer: FormControl = new FormControl('');
  filterControlDepartmentDefault: FormControl = new FormControl('');

  @Input() channel!: Channel;

  settingsAttendance: SettingsAttendance = {} as SettingsAttendance;

  listDepartmentToTransfer: Array<Department> = new Array<Department>();
  listDepartmentToDefault: Array<Department> = new Array<Department>();
  allDepartmens: Array<Department> = new Array<Department>();

  isInactive: boolean = false;

  isInactivityChatbot: boolean = false;

  // Fluxo de Atendimento — lives on SettingsModule (activeFlowModule + flow), not on SettingsAttendance
  settingsModule: SettingsModule | null = null;
  activeFlowModule: boolean = false;
  flow: string = '';
  flows: Array<{ _id: string; name: string }> = [];
  flowsError: string | null = null;

  constructor(
    injector: Injector,
    private service: SettingsAttendanceService,
    private settingsModuleService: SettingsModuleService,
    private flowService: FlowService,
    private departmentService: DepartmentService,
    public dialog: MatDialog,
  ) {
    super(injector);
    this.createForm();
    this.fetchFilterDepartment();
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes['channel']?.currentValue) {
      this.createForm();
      this.settingsAttendance = {} as SettingsAttendance;
      this.channel = changes['channel']?.currentValue;
      this.formGroup.patchValue({ channel: this.channel });
      this.getSettingsAttendance();
      this.loadDepartments();
      if (this.getModuleFlow()) {
        this.getSettingsModule();
        this.loadFlows();
      }
    }
  }

  private getSettingsModule(): void {
    this.settingsModuleService.getByChannel(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settingsModule: SettingsModule) => {
          this.settingsModule = settingsModule;
          this.activeFlowModule = settingsModule?.activeFlowModule ?? false;
          const flow = settingsModule?.flow as unknown;
          this.flow = (flow && typeof flow === 'object') ? (flow as { _id: string })._id : ((flow as string) ?? '');
        },
        error: (err) => this.alertService.error(err.error.message),
      });
  }

  private loadFlows(): void {
    this.flowsError = null;
    this.flowService.listPublishedForPicker()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list) => {
          this.flows = (list as Array<{ _id: string; name: string }>) ?? [];
          this.flowsError = this.flows.length ? null : 'Nenhum fluxo publicado disponível.';
        },
        error: () => {
          this.flowsError = 'Não foi possível carregar os fluxos.';
        },
      });
  }

  onActiveFlowModuleToggle(event: Event): void {
    this.activeFlowModule = (event.target as HTMLInputElement).checked;
  }

  changeSelectedFlow(event: any): void {
    this.flow = event.value;
  }

  getSettingsAttendance() {
    this.service.getByChannel(this.channel._id).subscribe({
      next: (value: SettingsAttendance) => {
        this.settingsAttendance = value;
        this.formGroup.patchValue(this.settingsAttendance);
      },
      error: (err) => this.alertService.error(err.error.message),
    });
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      minutesTransferAttendance: [1],
      tranferTo: [null],
      minutesFinishAttendance: [1],
      maxNumberAttendanceAttendant: [1],
      typeDistribution: [AttendanceDistributionEnum.LESS_ACTIVE_CONVERSATION, Validators.required],
      LESS_ACTIVE_CONVERSATION: [false],
      ALPHABETICAL_ORDER: [false],
      FIRST_ASSUME: [false],
      viewMessageBeforeAssume: [false],
      isAttendanceInactive: [false],
      closeAttendanceInactive: [false],
      limitAttendancePerAttendant: [false],
      sendResponseInactivy: [false],
      sendResponseAfterPeriod: [1],
      daySendResponse: [false],
      minutesSendResponse: [false],
      hourSendResponse: [false],
      autoAttendanceInactivy: [false],
      autoAfterPeriod: [1],
      departmentDefault: [null],
      periodToIgnoreAttendance: [1],
      checkedIgnoreAttendance: [false],
      amountAttemptedResponse: [1],
      finishAttendanceAfterAttemptedResponse: [false],
      filterIrrelevantMessages: [false],
      irrelevantWords: [''],
      preventReopenForEmojiOnly: [false],
      distributionForOnlineUsers: [false],
      distributionForOvertimeAttendance: [false]
    });
    this.formGroup.valueChanges.subscribe(value => {
      Object.assign(this.settingsAttendance, value);
    });
    if (!this.isAdmin()) {
      this.formGroup.disable();
    }
  }

  onConfirmClick(): void {
    // Fluxo de Atendimento: a flow must be selected to activate — there is no default.
    if (this.getModuleFlow() && this.activeFlowModule && !this.flow) {
      this.alertService.warning('Selecione um fluxo para ativar o Fluxo de Atendimento.');
      return;
    }
    this.loading = true;
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      this.loading = false;
      return;
    }
    this.service.update(this.settingsAttendance._id, this.settingsAttendance).subscribe({
      next: (value: SettingsAttendance) => {
        this.saveSettingsModuleFlow();
        this.alertService.success(this.t('shared.settingsAttendance.updateSuccess'));
        this.loading = false;
      },
      error: (err) => {
        this.alertService.error(err.error.message);
        this.loading = false;
      },
    });
  }

  // Persists the Fluxo de Atendimento block (SettingsModule.activeFlowModule + flow) alongside the
  // attendance settings — the two live on different documents but share the single Salvar button.
  private saveSettingsModuleFlow(): void {
    if (!this.getModuleFlow() || !this.settingsModule?._id) {
      return;
    }
    const patch = {
      ...this.settingsModule,
      activeFlowModule: this.activeFlowModule,
      flow: (this.flow || null) as unknown as SettingsModule['flow'],
    };
    this.settingsModuleService.update(this.settingsModule._id, patch)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => this.alertService.error(err.error.message),
      });
  }

  private loadDepartments() {
    this.departmentService.getDepartmentsByChannel(this.channel._id).subscribe({
      next: (value: Array<Department>) => {
        this.listDepartmentToTransfer = value;
        this.listDepartmentToDefault = value;
        this.allDepartmens = value;
      },
      error: (err) => this.alertService.error(err.error.message),
    });
  }

  private fetchFilterDepartment(): void {
    this.filterControlDepartmentToTransfer.valueChanges.pipe(
      debounceTime(300),
    ).subscribe((value: string) => {
      const currentValue = this.formGroup.get('tranferTo')?.value || null;

      this.listDepartmentToTransfer = this.allDepartmens.filter(department =>
        department.name.toLowerCase().includes(value.toLowerCase()) &&
        department._id !== currentValue
      );

      const existingDepartment = this.allDepartmens.find(department => department._id === currentValue);
      if (existingDepartment) {
        this.listDepartmentToTransfer.push(existingDepartment);
      }
    });

    this.filterControlDepartmentDefault.valueChanges.pipe(
      debounceTime(300),
    ).subscribe((value: string) => {
      const currentValue = this.formGroup.get('departmentDefault')?.value || null;

      this.listDepartmentToDefault = this.allDepartmens.filter(department =>
        department.name.toLowerCase().includes(value.toLowerCase()) &&
        department._id !== currentValue
      );

      const existingDepartment = this.allDepartmens.find(department => department._id === currentValue);
      if (existingDepartment) {
        this.listDepartmentToDefault.push(existingDepartment);
      }
    });
  }

  onFocusTransferTo(): void {
    this.listDepartmentToTransfer = this.allDepartmens;
  }

  onFocusDepartmentDefault(): void {
    this.listDepartmentToDefault = this.allDepartmens;
  }

  onDistribuitionToggle(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.formGroup.get('typeDistribution').setValue(AttendanceDistributionEnum.LESS_ACTIVE_CONVERSATION);
    } else {
      this.formGroup.get('typeDistribution').setValue(AttendanceDistributionEnum.FIRST_ASSUME);
    }
  }
  onListaToggle(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.formGroup.get('typeDistribution').setValue(AttendanceDistributionEnum.FIRST_ASSUME);
    } else {
      this.formGroup.get('typeDistribution').setValue(AttendanceDistributionEnum.LESS_ACTIVE_CONVERSATION);
    }
  }

  onAlphabeticalOrderToggle(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.formGroup.get('typeDistribution').setValue(AttendanceDistributionEnum.ALPHABETICAL_ORDER);
    } else {
      this.formGroup.get('typeDistribution').setValue(AttendanceDistributionEnum.LESS_ACTIVE_CONVERSATION);
    }
  }

  onDistributionForOnlineUsers(event: Event) {
    this.formGroup.get('distributionForOnlineUsers').setValue((event.target as HTMLInputElement).checked);
  }

  onDistributionForOvertimeAttendance(event: Event) {
    this.formGroup.get('distributionForOvertimeAttendance').setValue((event.target as HTMLInputElement).checked);
  }

  changeIsAttendanceInactive(changedControl: string) {
    if (changedControl === 'autoAttendanceInactivy') {
      this.formGroup.get('isAttendanceInactive').setValue(false, { emitEvent: false });
    } else {
      this.formGroup.get('autoAttendanceInactivy').setValue(false, { emitEvent: false });
    }

    const isAttendanceInactive = this.formGroup.get('isAttendanceInactive').value;
    if (isAttendanceInactive) {
      this.formGroup.get('minutesTransferAttendance').setValidators([Validators.required]);
      this.formGroup.get('tranferTo').setValidators([Validators.required]);
    } else {
      this.formGroup.get('minutesTransferAttendance').clearValidators();
      this.formGroup.get('tranferTo').clearValidators();
    }

    this.formGroup.get('minutesTransferAttendance').updateValueAndValidity();
    this.formGroup.get('tranferTo').updateValueAndValidity();
  }

  changeCloseAttendanceInactive() {
    const closeAttendanceInactive = this.formGroup.get('closeAttendanceInactive')?.value;
    if (closeAttendanceInactive) {
      this.formGroup.get('minutesFinishAttendance').setValidators([Validators.required]);
    } else {
      this.formGroup.get('minutesFinishAttendance').clearValidators();
    }
    this.formGroup.get('minutesFinishAttendance').updateValueAndValidity();
  }

  changeLimitAttendancePerAttendant() {
    const limitAttendancePerAttendant = this.formGroup.get('limitAttendancePerAttendant')?.value;
    if (limitAttendancePerAttendant) {
      this.formGroup.get('maxNumberAttendanceAttendant').setValidators([Validators.required]);
    } else {
      this.formGroup.get('maxNumberAttendanceAttendant').clearValidators();
    }
    this.formGroup.get('maxNumberAttendanceAttendant').updateValueAndValidity();
  }

  changeSelectOptionsInactivy(event: any): void {

    this.formGroup.patchValue({
      daySendResponse: false,
      minutesSendResponse: false,
      hourSendResponse: false,
    });

    this.formGroup.patchValue({
      [event.value]: true,
    });
  }

  addFilterIrrelevantMessages(): void {
    const dialogRef = this.dialog.open(IrrelevanceReopeningModalComponent, {
      width: '600px',
      data: {
        filterIrrelevantMessages: this.formGroup.get('filterIrrelevantMessages')?.value,
        irrelevantWords: this.formGroup.get('irrelevantWords')?.value,
        periodToIgnoreAttendance: this.formGroup.get('periodToIgnoreAttendance')?.value,
        preventReopenForEmojiOnly: this.formGroup.get('preventReopenForEmojiOnly')?.value,
        checkedIgnoreAttendance: this.formGroup.get('checkedIgnoreAttendance')?.value,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.formGroup.patchValue(result);
        this.formGroup.markAsDirty();
      }
    });
  }

  get selectedOptions(): string {
    if (this.formGroup.get('daySendResponse')?.value) {
      return 'daySendResponse';
    } else if (this.formGroup.get('hourSendResponse')?.value) {
      return 'hourSendResponse';
    } else if (this.formGroup.get('minutesSendResponse')?.value) {
      return 'minutesSendResponse';
    }
    return '';
  }
}
